import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner, Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaArrowLeft, FaChartBar, FaCalendarAlt, FaFilter } from "react-icons/fa";
import { useClientes } from '../../services/hooks/useClientes';
import { useComedores } from '../../services/hooks/useComedores';
import { reporteApi } from '../../services/api/reporteApi';
import { downloadExcelReport } from '../../services/utils/downloadHelper';
import "react-toastify/dist/ReactToastify.css";

const dh = {
  pageBg:
    "linear-gradient(165deg, #eef2ff 0%, #ecfeff 38%, #f5f3ff 72%, #f8fafc 100%)",
  heroCard:
    "linear-gradient(135deg, rgba(99, 102, 241, 0.09) 0%, rgba(45, 212, 191, 0.08) 50%, rgba(139, 92, 246, 0.07) 100%)",
  accent: "#4f46e5",
  accentSoft: "#6366f1",
  teal: "#0d9488",
  text: "#0f172a",
  textMuted: "#64748b",
  border: "rgba(148, 163, 184, 0.35)",
  cardShadow: "0 12px 40px -12px rgba(15, 23, 42, 0.12), 0 4px 16px -4px rgba(99, 102, 241, 0.08)",
  radiusLg: "1.25rem",
  radiusMd: "0.75rem",
};

const ReporteConsolidadoConsumosPorComensalCampamento = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));

  const { comedores, fetchComedoresByProyecto, loading: loadingComedores } = useComedores();
  const { clientes, fetchClientesByProyecto, loading: loadingClientes } = useClientes();

  const [formData, setFormData] = useState({
    fecha_servicio_desde: "",
    fecha_servicio_hasta: "",
    id_proyecto: selectedProject ? selectedProject.id : "",
    id_cliente: "",
    id_comedor: ""
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  useEffect(() => {
    if (currentUser?.id && selectedProject?.id) {
      fetchData();
    }
  }, [currentUser?.id, selectedProject?.id]);

  const fetchData = useCallback(async () => {
    try {
      await Promise.all([
        fetchComedoresByProyecto(selectedProject.id),
        fetchClientesByProyecto(selectedProject.id)
      ]);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error cargando datos iniciales');
    }
  }, [selectedProject?.id, fetchComedoresByProyecto, fetchClientesByProyecto]);

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateToYYYYMMDD = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (name, value) => {
    if (value) {
      const [year, month, day] = value.split('-');
      const formattedDate = `${day}/${month}/${year}`;
      setFormData(prev => ({ ...prev, [name]: formattedDate }));
    } else {
      setFormData(prev => ({ ...prev, [name]: '' }));
    }
  };

  const CustomDateInput = ({ label, name, value, required = false }) => {
    return (
      <Form.Group className="mb-3">
        <Form.Label
          className="d-flex align-items-center gap-2 small fw-semibold mb-2"
          style={{ color: dh.text, letterSpacing: '0.02em' }}
        >
          <FaCalendarAlt style={{ color: dh.teal, opacity: 0.9 }} />
          {label}
        </Form.Label>
        <Form.Control
          type="date"
          name={name}
          value={value ? formatDateToYYYYMMDD(value) : ''}
          onChange={(e) => handleDateChange(name, e.target.value)}
          required={required}
          style={{
            background: '#fff',
            border: `1px solid ${dh.border}`,
            borderRadius: dh.radiusMd,
            minHeight: '2.75rem',
            color: dh.text,
          }}
        />
      </Form.Group>
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (prev[name] !== value) {
        return { ...prev, [name]: value };
      }
      return prev;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fecha_servicio_desde || !formData.fecha_servicio_hasta) {
      toast.error('Por favor seleccione ambas fechas');
      return;
    }

    if (new Date(formatDateToYYYYMMDD(formData.fecha_servicio_desde)) > new Date(formatDateToYYYYMMDD(formData.fecha_servicio_hasta))) {
      toast.error('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }

    setLoading(true);
    try {
      const params = {
        fecha_servicio_desde: formatDateToYYYYMMDD(formData.fecha_servicio_desde),
        fecha_servicio_hasta: formatDateToYYYYMMDD(formData.fecha_servicio_hasta),
        id_proyecto: formData.id_proyecto,
      };
      if (formData.id_cliente) {
        params.id_cliente = formData.id_cliente;
      }
      if (formData.id_comedor) {
        params.id_comedor = formData.id_comedor;
      }

      const fileName = `Consolidado_Consumos_Comensal_Campamento_${formatDateToDDMMYYYY(formData.fecha_servicio_desde)}_${formatDateToDDMMYYYY(formData.fecha_servicio_hasta)}.xlsx`;

      await downloadExcelReport(
        reporteApi.descargarReporteConsolidadoConsumosComensalCampamento,
        params,
        fileName
      );

      toast.success('Reporte generado exitosamente');
    } catch (error) {
      console.error('Error generando reporte:', error);
      toast.error('Error generando el reporte');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    borderRadius: dh.radiusMd,
    border: `1px solid ${dh.border}`,
    minHeight: '2.75rem',
  };

  const labelStyle = { color: dh.text, fontWeight: 600, fontSize: '0.875rem', letterSpacing: '0.02em' };

  return (
    <div className="min-vh-100" style={{ background: dh.pageBg }}>
      <ToastContainer />

      <Modal
        show={loading}
        backdrop="static"
        keyboard={false}
        centered
        contentClassName="border-0"
        style={{ fontFamily: 'system-ui, "Segoe UI", sans-serif' }}
        aria-labelledby="reporte-consolidado-campamento-loading-title"
      >
        <Modal.Body
          className="text-center py-5 px-4"
          style={{
            borderRadius: dh.radiusLg,
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: dh.cardShadow,
          }}
        >
          <div
            className="d-flex justify-content-center mb-3 mx-auto rounded-circle align-items-center"
            style={{
              width: 72,
              height: 72,
              background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(45,212,191,0.18))',
            }}
          >
            <Spinner
              animation="border"
              role="status"
              style={{ width: '2rem', height: '2rem', color: dh.accent }}
            />
          </div>
          <FaFileExcel className="mb-3" style={{ fontSize: '1.75rem', color: dh.teal }} aria-hidden />
          <h5 id="reporte-consolidado-campamento-loading-title" className="mb-2 fw-semibold" style={{ color: dh.text }}>
            Generando el reporte Excel
          </h5>
          <p className="mb-0 small" style={{ color: dh.textMuted, maxWidth: 320, margin: '0 auto', lineHeight: 1.55 }}>
            Estamos preparando el archivo. Con rangos amplios puede tardar unos minutos; mantenga esta ventana abierta.
          </p>
        </Modal.Body>
      </Modal>

      <Container className="py-4 py-md-5" style={{ maxWidth: 920 }}>
        <div
          className="p-4 p-md-4 mb-4"
          style={{
            background: dh.heroCard,
            borderRadius: dh.radiusLg,
            border: `1px solid ${dh.border}`,
            boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset',
          }}
        >
          <Row className="align-items-center g-3">
            <Col xs="auto">
              <Button
                onClick={() => navigate('/reportes')}
                className="d-inline-flex align-items-center border-0 fw-semibold"
                style={{
                  background: '#fff',
                  color: dh.text,
                  borderRadius: dh.radiusMd,
                  padding: '0.5rem 1rem',
                  boxShadow: '0 2px 12px rgba(15, 23, 42, 0.06)',
                }}
              >
                <FaArrowLeft className="me-2" style={{ color: dh.accentSoft }} />
                Volver
              </Button>
            </Col>
            <Col>
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-none d-sm-flex flex-shrink-0 align-items-center justify-content-center rounded-3"
                  style={{
                    width: 48,
                    height: 48,
                    background: 'linear-gradient(135deg, #6366f1, #14b8a6)',
                    boxShadow: '0 8px 20px -6px rgba(99, 102, 241, 0.45)',
                  }}
                >
                  <FaChartBar className="text-white" style={{ fontSize: '1.15rem' }} />
                </div>
                <div>
                  <h1 className="h4 mb-1 fw-semibold" style={{ color: dh.text, letterSpacing: '-0.02em' }}>
                    Consolidado de consumos por comensal
                  </h1>
                  <p className="mb-0 small" style={{ color: dh.textMuted, lineHeight: 1.5 }}>
                    Matriz de consumos por fechas, comedor y tipo de servicio. Exportación a Excel.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <Row className="justify-content-center">
          <Col lg={9} xl={8}>
            <Card
              className="border-0"
              style={{
                borderRadius: dh.radiusLg,
                boxShadow: dh.cardShadow,
                overflow: 'hidden',
              }}
            >
              <Card.Header
                className="border-0 py-3 px-4"
                style={{
                  background: 'linear-gradient(90deg, rgba(99,102,241,0.06) 0%, rgba(45,212,191,0.05) 100%)',
                  borderBottom: `1px solid ${dh.border}`,
                }}
              >
                <h2 className="h6 mb-0 d-flex align-items-center gap-2 fw-semibold" style={{ color: dh.text }}>
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-2"
                    style={{
                      width: 32,
                      height: 32,
                      background: 'rgba(99, 102, 241, 0.12)',
                      color: dh.accent,
                    }}
                  >
                    <FaFilter style={{ fontSize: '0.85rem' }} />
                  </span>
                  Filtros
                </h2>
              </Card.Header>
              <Card.Body className="p-4 p-md-4" style={{ background: '#fff' }}>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <CustomDateInput
                        label="Fecha desde"
                        name="fecha_servicio_desde"
                        value={formData.fecha_servicio_desde}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <CustomDateInput
                        label="Fecha hasta"
                        name="fecha_servicio_hasta"
                        value={formData.fecha_servicio_hasta}
                        required
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Cliente</Form.Label>
                        <Form.Select
                          name="id_cliente"
                          value={formData.id_cliente}
                          onChange={handleInputChange}
                          style={inputStyle}
                        >
                          <option value="">Todos los clientes</option>
                          {loadingClientes ? (
                            <option disabled>Cargando clientes...</option>
                          ) : (
                            clientes.map((cliente) => (
                              <option key={cliente.id} value={cliente.id}>
                                {cliente.nombre_cliente || cliente.nombre || `Cliente #${cliente.id}`}
                              </option>
                            ))
                          )}
                        </Form.Select>
                        <Form.Text style={{ color: dh.textMuted, fontSize: '0.8rem' }}>
                          Solo clientes del proyecto actual
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Comedor</Form.Label>
                        <Form.Select
                          name="id_comedor"
                          value={formData.id_comedor}
                          onChange={handleInputChange}
                          style={inputStyle}
                        >
                          <option value="">Todos los comedores</option>
                          {loadingComedores ? (
                            <option disabled>Cargando comedores...</option>
                          ) : (
                            comedores.map(comedor => (
                              <option key={comedor.id} value={comedor.id}>
                                {comedor.nombre}
                              </option>
                            ))
                          )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-grid pt-1">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading}
                      className="d-flex align-items-center justify-content-center border-0 fw-semibold py-3"
                      style={{
                        borderRadius: dh.radiusMd,
                        background: loading
                          ? '#94a3b8'
                          : 'linear-gradient(120deg, #4f46e5 0%, #0d9488 55%, #6366f1 100%)',
                        boxShadow: loading
                          ? 'none'
                          : '0 10px 28px -8px rgba(79, 70, 229, 0.45), 0 4px 12px -4px rgba(13, 148, 136, 0.25)',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generando…
                        </>
                      ) : (
                        <>
                          <FaFileExcel className="me-2" />
                          Descargar Excel
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ReporteConsolidadoConsumosPorComensalCampamento;
